import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Modal, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import SectionLoader from "../../../components/SectionLoader";
import { error, success } from "../../../notify";
import axios from 'axios';
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import JoditEditor from "jodit-react";
import { TIMEZONE_MAP } from "./timezones";

const Configurations = () => {
  const [configData, setConfigData] = useState([]);
  const [, setFilteredConfigs] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showValueModal, setShowValueModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewingValue, setViewingValue] = useState("");
  const [viewingName, setViewingName] = useState("");
  const [editValue, setEditValue] = useState("");
  const [editAdmin, setEditAdmin] = useState(false);
  const [groupedConfigs, setGroupedConfigs] = useState({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [showClearButton, setShowClearButton] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const location = useLocation();
  const { siteId, siteName } = location.state || {};
  const apiBaseUrl = process.env.REACT_APP_SERVER_URL;

  const navigate = useNavigate();

  useEffect(() => {
    const fetchConfigurations = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${apiBaseUrl}/api/website/configurations/${siteId}`);
        const data = response.data || [];
        console.log("Fetched data:", data);
        setConfigData(data);
        setFilteredConfigs(data);

        // Group configurations by category
        const grouped = data.reduce((acc, item) => {
          const category = item.category || "Uncategorized";
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push(item);
          return acc;
        }, {});

        setGroupedConfigs(grouped);
      } catch (err) {
        error(err.response?.data?.message || "Failed to fetch configurations.", true);
        console.error("Error fetching data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (siteId) {
      fetchConfigurations();
    }
  }, [siteId, apiBaseUrl]);

  // Search functionality
  const handleSearch = () => {
    if (searchInputValue.trim() !== "") {
      setShowClearButton(true);
      const filtered = configData.filter(item =>
        item.config_name.toLowerCase().includes(searchInputValue.toLowerCase()) ||
        item.value.toLowerCase().includes(searchInputValue.toLowerCase()) ||
        (item.category && item.category.toLowerCase().includes(searchInputValue.toLowerCase()))
      );
      setFilteredConfigs(filtered);

      // Group the filtered results
      const grouped = filtered.reduce((acc, item) => {
        const category = item.category || "Uncategorized";
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(item);
        return acc;
      }, {});
      setGroupedConfigs(grouped);
    }
  };

  const handleClearSearch = () => {
    setSearchInputValue("");
    setShowClearButton(false);
    setFilteredConfigs(configData);

    // Re-group all data when search is cleared
    const grouped = configData.reduce((acc, item) => {
      const category = item.category || "Uncategorized";
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(item);
      return acc;
    }, {});
    setGroupedConfigs(grouped);
  };

  const handleSearchInputChange = (event) => {
    setSearchInputValue(event.target.value);
    if (event.target.value.trim() === "") {
      setShowClearButton(false);
      setFilteredConfigs(configData);

      // Re-group all data when search is cleared
      const grouped = configData.reduce((acc, item) => {
        const category = item.category || "Uncategorized";
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(item);
        return acc;
      }, {});
      setGroupedConfigs(grouped);
    }
  };

  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  // Refresh functionality
  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/api/website/configurations/${siteId}`);
      const data = response.data || [];
      setConfigData(data);
      setFilteredConfigs(data);

      const grouped = data.reduce((acc, item) => {
        const category = item.category || "Uncategorized";
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(item);
        return acc;
      }, {});
      setGroupedConfigs(grouped);

      success("Configurations refreshed successfully.", true);
    } catch (err) {
      error(err.response?.data?.message || "Failed to refresh configurations.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleShowEditModal = (item, categoryIndex, itemIndex) => {
    setEditingItem({ ...item, categoryIndex, itemIndex });
    setEditValue(item.value);
    setEditAdmin(item.site_config);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingItem(null);
  };

  const handleShowValueModal = (value, name) => {
    setViewingValue(value);
    setViewingName(name);
    setShowValueModal(true);
  };

  const handleCloseValueModal = () => {
    setShowValueModal(false);
    setViewingValue("");
    setViewingName("");
  };

  const handleSaveChanges = async () => {
    if (!editingItem) return;

    try {
      // Create a copy of the item with updated values
      const updatedItem = {
        ...editingItem,
        value: editValue,
        site_config: editAdmin
      };

      // Updated API call to send site_id as a query parameter
      console.log("config_value:", editValue);
      await axios.put(
        `${apiBaseUrl}/api/website/configurations/${siteId}`,
        {
          parameter_name: editingItem.config_name,
          admin: editAdmin,
          config_value: editValue
        },
        {
          params: {
            site_id: siteId  // Adding site_id as query parameter
          }
        }
      );

      // Update the local state
      const category = editingItem.category || "Uncategorized";
      const updatedConfigData = [...configData];
      const itemIndex = configData.findIndex(item => item.config_name === editingItem.config_name);
      if (itemIndex !== -1) {
        updatedConfigData[itemIndex] = updatedItem;
        setConfigData(updatedConfigData);
        setFilteredConfigs(updatedConfigData);

        // Update the grouped configs too
        const updatedGroupedConfigs = { ...groupedConfigs };
        updatedGroupedConfigs[category] = updatedGroupedConfigs[category].map((item, idx) =>
          item.config_name === editingItem.config_name ? updatedItem : item
        );
        setGroupedConfigs(updatedGroupedConfigs);
      }

      success("Configuration updated successfully", true);
      handleCloseEditModal();
    } catch (err) {
      error(err.response?.data?.message || "Failed to update configuration");
      console.error("Error updating data:", err);
    }
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return "N/A";

    // For HTML content, create a temporary div to extract text content
    if (text && text.includes('<')) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = text;
      const textContent = tempDiv.textContent || tempDiv.innerText || '';
      return textContent.length > maxLength
        ? `${textContent.substring(0, maxLength)}...`
        : textContent;
    }

    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  // Determine if a string contains HTML
  const isHTML = (str) => {
    return /<[a-z][\s\S]*>/i.test(str);
  };

  // Jodit editor configuration
  const joditConfig = useMemo(
    () => ({
      readonly: false,
      buttonsMD: ["bold", "italic", "|", "ul", "ol", "link", "image"],
      buttonsSM: ["bold", "italic", "|", "ul", "ol", "link", "image"],
      buttonsXS: ["bold", "italic", "|", "ul", "ol", "link", "image"],
      statusbar: false,
      link: {
        openInNewTabCheckbox: true,
        noFollowCheckbox: false,
      },
    }),
    []
  );

  // Icons
  const RefreshIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 2v6h6"></path>
      <path d="M21 12a9 9 0 0 0-15-6.7L3 8"></path>
      <path d="M21 22v-6h-6"></path>
      <path d="M3 12a9 9 0 0 0 15 6.7L21 16"></path>
    </svg>
  );

  const BackIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6"></polyline>
      <line x1="5" y1="6" x2="5" y2="18"></line>
    </svg>
  );

  const EditIcon = () => (
    <svg width="16" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M13.7476 20.4428H21.0002" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path fillRule="evenodd" clipRule="evenodd" d="M12.78 3.79479C13.5557 2.86779 14.95 2.73186 15.8962 3.49173C15.9485 3.53296 17.6295 4.83879 17.6295 4.83879C18.669 5.46719 18.992 6.80311 18.3494 7.82259C18.3153 7.87718 8.81195 19.7645 8.81195 19.7645C8.49578 20.1589 8.01583 20.3918 7.50291 20.3973L3.86353 20.443L3.04353 16.9723C2.92866 16.4843 3.04353 15.9718 3.3597 15.5773L12.78 3.79479Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M11.021 6.00098L16.4732 10.1881" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const SearchIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"></circle>
      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
    </svg>
  );

  const ClearIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );

  return (
    <div style={{ fontFamily: '"Inter", sans-serif', background: '#f8f9fd', margin: 0, padding: '20px', minHeight: '100vh' }}>
      <div style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', padding: '20px' }}>
        {/* Header Section */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap' }}>
          <h2 style={{ fontSize: '20px', margin: 0, fontWeight: 600 }}>
            Configurations - {siteName}
          </h2>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Search Box with Button */}
            <div style={{ position: 'relative', width: '350px' }}>
              <input
                type="text"
                placeholder="Search configurations..."
                value={searchInputValue}
                onChange={handleSearchInputChange}
                onKeyPress={handleSearchKeyPress}
                style={{
                  width: '100%',
                  padding: '8px 68px 8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
              <div style={{
                position: 'absolute',
                right: '4px',
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                gap: '4px',
                alignItems: 'center'
              }}>
                {showClearButton && (
                  <Button
                    variant="outline-secondary"
                    onClick={handleClearSearch}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '8px',
                      width: '28px',
                      height: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0',
                      cursor: 'pointer',
                      color: '#666',
                      transition: 'all 0.2s ease-in-out',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f0f0f0';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <ClearIcon />
                  </Button>
                )}
                <Button
                  variant="primary"
                  onClick={handleSearch}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '8px',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0',
                    cursor: 'pointer',
                    color: '#666',
                    transition: 'all 0.2s ease-in-out',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f0f0f0';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <SearchIcon />
                </Button>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <OverlayTrigger placement="top" overlay={<Tooltip>Refresh</Tooltip>}>
              <Button
                variant="outline-secondary"
                onClick={handleRefresh}
                disabled={isLoading}
                style={{
                  background: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0',
                  fontSize: '14px',
                  color: '#5a6acf',
                  cursor: 'pointer',
                }}
              >
                <RefreshIcon />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={<Tooltip>Back to Sites</Tooltip>}>
              <Button
                variant="outline-secondary"
                onClick={() => navigate(-1)}
                style={{
                  background: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0',
                  fontSize: '14px',
                  color: '#5a6acf',
                  cursor: 'pointer',
                  flexShrink: 0
                }}
              >
                <BackIcon />
              </Button>
            </OverlayTrigger>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <SectionLoader label="Loading configurations..." />
        )}

        {/* Configuration Items Section */}
        {!isLoading && Object.keys(groupedConfigs).length > 0 ? (
          Object.keys(groupedConfigs).map((category, categoryIndex) => (
            <div key={categoryIndex} className="mb-5">
              <h5 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '20px', color: '#333', borderBottom: '2px solid #5a6acf', paddingBottom: '8px' }}>
                {category}
              </h5>

              {/* Individual Configuration Items */}
              <div style={{ display: 'grid', gap: '16px' }}>
                {groupedConfigs[category].map((item, itemIndex) => (
                  <div
                    key={itemIndex}
                    style={{
                      background: '#fff',
                      border: '1px solid #e1e5e9',
                      borderRadius: '12px',
                      padding: '20px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.06)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '20px' }}>

                      {/* Left Section - Configuration Name */}
                      <div style={{ flex: '0 0 40%' }}>
                        <h6 style={{
                          fontSize: '16px',
                          fontWeight: 600,
                          margin: '0 0 8px 0',
                          color: '#333',
                          lineHeight: '1.4'
                        }}>
                          {item.config_name}
                        </h6>
                        {item.description && (
                          <p style={{
                            fontSize: '14px',
                            color: '#666',
                            margin: 0,
                            lineHeight: '1.4'
                          }}>
                            {item.description}
                          </p>
                        )}
                      </div>

                      {/* Middle Section - Value */}
                      <div style={{ flex: '1', minWidth: '0' }}>
                        <label style={{
                          fontSize: '12px',
                          fontWeight: 500,
                          color: '#888',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          display: 'block',
                          marginBottom: '6px'
                        }}>
                          Current Value
                        </label>
                        <div
                          style={{
                            background: '#f8f9fa',
                            padding: '12px 16px',
                            borderRadius: '8px',
                            border: '1px solid #e9ecef',
                            fontSize: '14px',
                            color: '#333',
                            cursor: 'pointer',
                            overflow: 'hidden',
                            wordBreak: 'break-word',
                            lineHeight: '1.4'
                          }}
                          onClick={() => handleShowValueModal(item.value, item.config_name)}
                        >
                          {item.config_name === "chatbot_active_status" ? (
                            (String(item.value).toLowerCase() === "true" ? "Live" : "Development")
                          ) : item.config_type === "TIMEZONE" ? (
                            Object.entries(TIMEZONE_MAP).find(([, v]) => v === item.value)?.[0] || item.value
                          ) : isHTML(item.value) ? (
                            <div dangerouslySetInnerHTML={{ __html: truncateText(item.value, 100) }} />
                          ) : (
                            truncateText(item.value, 100)
                          )}
                        </div>
                      </div>

                      {/* Right Section - Actions */}
                      <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <OverlayTrigger placement="top" overlay={<Tooltip>Edit Configuration</Tooltip>}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowEditModal(item, categoryIndex, itemIndex);
                            }}
                            style={{
                              background: 'white',
                              border: '1px solid #e1e5e9',
                              borderRadius: '8px',
                              width: '36px',
                              height: '36px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              color: '#5a6acf',
                              transition: 'all 0.2s ease',
                              boxShadow: '0 2px 6px rgba(90, 106, 207, 0.4)',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#f8f9fd';
                              e.currentTarget.style.borderColor = '#5a6acf';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'white';
                              e.currentTarget.style.borderColor = '#e1e5e9';
                            }}
                          >
                            <EditIcon />
                          </button>
                        </OverlayTrigger>
                      </div>
                    </div>

                    {/* Optional: Type indicator */}
                    {item.config_type && (
                      <div style={{ marginTop: '12px' }}>
                        <span style={{
                          fontSize: '12px',
                          background: '#e3f2fd',
                          color: '#1976d2',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontWeight: 500
                        }}>
                          {item.config_type}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : (
          !isLoading && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
              {configData.length === 0 ? 'No configuration data available.' : 'No configurations found matching your search.'}
            </div>
          )
        )}
      </div>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={handleCloseEditModal}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Configuration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingItem && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Configuration Name</Form.Label>
                <Form.Control
                  type="text"
                  value={editingItem.config_name}
                  disabled
                />
              </Form.Group>

              {/* Conditional rendering for dropdown or textarea */}
              {editingItem.config_type === "BOOLEAN" ? (
                <Form.Group className="mb-3" style={{ position: "relative" }}>
                  <Form.Label>Value</Form.Label>

                  <div
                    className="custom-dropdown"
                    onClick={() => setIsDropdownOpen((prev) => !prev)}
                    style={{ position: "relative", display: "inline-block", width: "100%" }}
                  >
                    <Form.Control
                      as="select"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={() => setIsDropdownOpen(false)}
                      onFocus={() => setIsDropdownOpen(true)}
                      style={{ appearance: "none", paddingRight: "30px" }}
                    >
                      {editingItem.config_name === "chatbot_active_status" ? (
                        <>
                          {/* Keep the stored values True/False, just change labels */}
                          <option value="True">Live</option>
                          <option value="False">Development</option>
                        </>
                      ) : (
                        <>
                          <option value="True">True</option>
                          <option value="False">False</option>
                        </>
                      )}
                    </Form.Control>

                    <span
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                        color: "#555",
                      }}
                    >
                      {isDropdownOpen ? <FaChevronUp /> : <FaChevronDown />}
                    </span>
                  </div>
                </Form.Group>
              ) : editingItem.config_type === "TIMEZONE" ? (

                /* TIMEZONE DROPDOWN */

                <Form.Group className="mb-3" style={{ position: "relative" }}>
                  <Form.Label>Select Timezone</Form.Label>

                  <div
                    className="custom-dropdown"
                    onClick={() => setIsDropdownOpen((prev) => !prev)}
                    style={{ position: "relative", display: "inline-block", width: "100%" }}
                  >
                    <Form.Control
                      as="select"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={() => setIsDropdownOpen(false)}
                      onFocus={() => setIsDropdownOpen(true)}
                      style={{ appearance: "none", paddingRight: "30px" }}
                    >
                      {Object.entries(TIMEZONE_MAP).map(([label, value]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </Form.Control>

                    <span
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                        color: "#555",
                      }}
                    >
                      {isDropdownOpen ? <FaChevronUp /> : <FaChevronDown />}
                    </span>
                  </div>
                </Form.Group>

              ) : editingItem.config_type === "HTML" ? (
                <Form.Group className="mb-3">
                  <Form.Label>Value</Form.Label>
                  <div className="html-editor-container">
                    <JoditEditor
                      value={editValue}
                      config={joditConfig}
                      tabIndex={1}
                      onBlur={(newContent) => setEditValue(newContent)}
                    />
                  </div>
                </Form.Group>
              ) : (
                <Form.Group className="mb-3">
                  <Form.Label>Value</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    style={{ resize: "none" }}
                  />
                </Form.Group>
              )}
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseEditModal}
            style={{ border: '1px solid #ddd', minWidth: '100px' }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveChanges}
            style={{
              backgroundColor: '#5a6acf',
              borderColor: '#5a6acf',
              minWidth: '100px'
            }}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Value Preview Modal */}
      <Modal show={showValueModal} onHide={handleCloseValueModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{viewingName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ padding: '15px', background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px' }}>
            {isHTML(viewingValue) ? (
              <div dangerouslySetInnerHTML={{ __html: viewingValue || "No value available" }} />
            ) : (
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, fontFamily: 'inherit' }}>
                {viewingValue || "No value available"}
              </pre>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseValueModal}
            style={{ border: '1px solid #ddd' }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Configurations;